import React from 'react';

const DynamicFormSectionsContext = React.createContext(undefined);

export function useDynamicFormSections() {
  const context = React.useContext(DynamicFormSectionsContext);
  if (!context) throw new Error('useDynamicFormSections must be used inside DynamicFormSections');
  return context;
}

export default function DynamicFormSections({ children }: React.PropsWithChildren) {
  const [sections, setSections] = React.useState({});
  const [active, setActive] = React.useState('');

  const register = React.useCallback(() => {
    const id = window.crypto.randomUUID();

    setSections((s) => ({
      ...s,
      [id]: { visible: Object.keys(s).length === 0 },
    }));

    return id;
  }, []);

  const toggle = React.useCallback((id: string) => {
    if (!sections[id].visible) {
      setSections((s) => ({
        ...s,
        [active]: { ...s[active], visible: false },
        [id]: { ...s[id], visible: true },
      }));
    }
  }, [active, sections]);

  React.useEffect(() => {
    Object.keys(sections).forEach((key) => {
      const section = sections[key];
      if (section.visible) setActive(key);
    });
  }, [sections]);

  const values = React.useMemo(() => ({
    toggle,
    register,
    sections,
  }), [register, sections, toggle]);

  return (
    <DynamicFormSectionsContext.Provider value={values}>
      {children}
    </DynamicFormSectionsContext.Provider>
  );
}
